import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Lock, Eye, Database, UserCheck, Bell, Mail, Phone, MapPin, FileText, AlertCircle } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { companyInfo } from '../data/mock';

const PrivacyPolicyPage = () => {
  const lastUpdated = 'June 23, 2026';
  const effectiveDate = 'June 23, 2026';

  const sections = [
    {
      id: 'introduction',
      title: 'Introduction',
      icon: Shield,
      content: (
        <>
          <p className="mb-4">
            Nanda Diagnostics ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website, use our mobile application, or use our diagnostic services.
          </p>
          <p className="mb-4">
            This Privacy Policy applies to all information collected through our website (nandadiagnostics.com), mobile application, and any related services, sales, marketing, or events (collectively, the "Services").
          </p>
          <p className="mb-4">
            <strong>Please read this privacy policy carefully.</strong> If you do not agree with the terms of this privacy policy, please do not access the Services. By accessing or using our Services, you agree to this privacy policy.
          </p>
          
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
            <h5 className="font-semibold text-amber-800 mb-2">Important Medical Disclaimer</h5>
            <p className="text-amber-700 text-sm">
              Nanda Diagnostics provides diagnostic testing services only. Our services, website, and mobile application are <strong>NOT</strong> intended to provide medical advice, diagnosis, or treatment recommendations. Test results should always be interpreted by qualified healthcare professionals. We do not function as a medical device. Always consult with your physician or qualified healthcare provider for medical concerns.
            </p>
          </div>
        </>
      )
    },
    {
      id: 'regulatory-compliance',
      title: 'Regulatory Compliance',
      icon: Shield,
      content: (
        <>
          <p className="mb-4">
            Nanda Diagnostics operates in compliance with applicable data protection laws and healthcare regulations:
          </p>
          
          <h4 className="font-semibold text-gray-800 mb-3">Data Protection Laws</h4>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li><strong>Digital Personal Data Protection Act, 2023 (DPDP Act):</strong> We comply with India's data protection framework for processing digital personal data</li>
            <li><strong>Information Technology Act, 2000:</strong> We follow reasonable security practices as mandated under IT Rules</li>
            <li><strong>GDPR Principles:</strong> We adhere to international data protection standards for users worldwide</li>
          </ul>

          <h4 className="font-semibold text-gray-800 mb-3">Healthcare Regulations</h4>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li><strong>Clinical Establishments Act:</strong> Our diagnostic centers are registered and comply with applicable clinical establishment regulations</li>
            <li><strong>Medical Records Retention:</strong> We retain medical records as per applicable healthcare regulations (minimum 10 years)</li>
            <li><strong>Laboratory Accreditation:</strong> Our laboratories follow NABL/CAP quality standards</li>
          </ul>

          <h4 className="font-semibold text-gray-800 mb-3">Our Role as Data Fiduciary</h4>
          <p className="mb-4">
            Under the DPDP Act, Nanda Diagnostics acts as a "Data Fiduciary" - we determine the purpose and means of processing your personal data. We are responsible for ensuring your data is processed lawfully, fairly, and transparently.
          </p>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-blue-800 text-sm">
              <strong>Not a Medical Device:</strong> Our mobile application and website are informational tools for accessing your diagnostic reports and booking services. They are not registered medical devices and should not be used for self-diagnosis or treatment decisions.
            </p>
          </div>
        </>
      )
    },
    {
      id: 'data-safety-summary',
      title: 'Data Safety Summary',
      icon: Shield,
      content: (
        <>
          <p className="mb-4">
            This summary provides a quick overview of our data practices as required by app stores:
          </p>
          
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h5 className="font-semibold text-green-800 mb-2">Data Encrypted in Transit</h5>
              <p className="text-green-700 text-sm">All data transmitted between your device and our servers is encrypted using TLS 1.3</p>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h5 className="font-semibold text-green-800 mb-2">Data Deletion Available</h5>
              <p className="text-green-700 text-sm">You can request deletion of your account and data at any time</p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h5 className="font-semibold text-blue-800 mb-2">Data Collected</h5>
              <p className="text-blue-700 text-sm">Personal info, Health info, Financial info, Device info, App activity</p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h5 className="font-semibold text-blue-800 mb-2">Data Sharing</h5>
              <p className="text-blue-700 text-sm">Shared with service providers, healthcare partners (with consent), and as required by law</p>
            </div>
          </div>

          <h4 className="font-semibold text-gray-800 mb-3">Data Types Collected</h4>
          <ul className="list-disc pl-6 mb-4 space-y-1 text-sm">
            <li><strong>Personal Information:</strong> Name, email, phone, address (Required)</li>
            <li><strong>Health Information:</strong> Medical history, test results, prescriptions (Required for services)</li>
            <li><strong>Financial Information:</strong> Payment details, transaction history (Required for payments)</li>
            <li><strong>Device Information:</strong> Device ID, OS version, app version (Automatic)</li>
            <li><strong>Location:</strong> Approximate location for nearby centers (Optional)</li>
            <li><strong>Photos:</strong> Prescription/document images you upload (Optional)</li>
          </ul>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-amber-800 text-sm">
              <strong>No Data Sold:</strong> We do not sell your personal data to third parties for advertising or marketing purposes.
            </p>
          </div>
        </>
      )
    },
    {
      id: 'information-collected',
      title: 'Information We Collect',
      icon: Database,
      content: (
        <>
          <h4 className="font-semibold text-gray-800 mb-3">Personal Information You Provide</h4>
          <p className="mb-4">We collect personal information that you voluntarily provide when you:</p>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>Register for an account or use our services</li>
            <li>Book diagnostic tests or health packages</li>
            <li>Access your medical reports online</li>
            <li>Contact us through our website or application</li>
            <li>Subscribe to our newsletter or promotional communications</li>
            <li>Participate in surveys or provide feedback</li>
          </ul>
          
          <p className="mb-3"><strong>This information may include:</strong></p>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li><strong>Identity Data:</strong> Name, date of birth, gender, government ID numbers</li>
            <li><strong>Contact Data:</strong> Email address, phone number, residential address</li>
            <li><strong>Health Data:</strong> Medical history, test results, diagnostic reports, prescriptions</li>
            <li><strong>Financial Data:</strong> Payment card details, billing address, transaction history</li>
            <li><strong>Account Data:</strong> Username, password, account preferences</li>
          </ul>

          <h4 className="font-semibold text-gray-800 mb-3">Information Automatically Collected</h4>
          <p className="mb-4">When you access our Services, we automatically collect certain information:</p>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li><strong>Device Information:</strong> Device type, operating system, unique device identifiers</li>
            <li><strong>Log Data:</strong> IP address, browser type, pages visited, time spent on pages</li>
            <li><strong>Location Data:</strong> Approximate location based on IP address (we do not collect precise GPS location without explicit consent)</li>
            <li><strong>Usage Data:</strong> Features used, actions taken, time and date of access</li>
          </ul>

          <h4 className="font-semibold text-gray-800 mb-3">Information from Third Parties</h4>
          <p>We may receive information about you from third parties including:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Healthcare providers who refer you to our services</li>
            <li>Insurance companies for claim processing</li>
            <li>Payment processors for transaction verification</li>
          </ul>
        </>
      )
    },
    {
      id: 'how-we-use',
      title: 'How We Use Your Information',
      icon: Eye,
      content: (
        <>
          <p className="mb-4">We use the information we collect for the following purposes:</p>
          
          <h4 className="font-semibold text-gray-800 mb-3">To Provide Our Services</h4>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>Process and manage your diagnostic test bookings</li>
            <li>Generate and deliver your medical reports</li>
            <li>Enable secure access to your health records</li>
            <li>Process payments and send transaction confirmations</li>
            <li>Provide customer support and respond to inquiries</li>
          </ul>

          <h4 className="font-semibold text-gray-800 mb-3">To Improve Our Services</h4>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>Analyze usage patterns to enhance user experience</li>
            <li>Develop new features and services</li>
            <li>Conduct research and analytics</li>
            <li>Monitor and improve service quality</li>
          </ul>

          <h4 className="font-semibold text-gray-800 mb-3">For Communication</h4>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>Send appointment reminders and test result notifications</li>
            <li>Provide important updates about our services</li>
            <li>Send promotional communications (with your consent)</li>
            <li>Respond to your comments and questions</li>
          </ul>

          <h4 className="font-semibold text-gray-800 mb-3">For Legal and Safety Purposes</h4>
          <ul className="list-disc pl-6 space-y-2">
            <li>Comply with applicable laws and regulations</li>
            <li>Protect against fraudulent or illegal activity</li>
            <li>Enforce our terms and conditions</li>
            <li>Protect the rights and safety of our users and others</li>
          </ul>
        </>
      )
    },
    {
      id: 'data-sharing',
      title: 'How We Share Your Information',
      icon: UserCheck,
      content: (
        <>
          <p className="mb-4">We may share your information in the following circumstances:</p>

          <h4 className="font-semibold text-gray-800 mb-3">With Your Consent</h4>
          <p className="mb-4">We will share your personal information with third parties when you have given us explicit consent to do so.</p>

          <h4 className="font-semibold text-gray-800 mb-3">Service Providers</h4>
          <p className="mb-4">We may share your information with third-party service providers who perform services on our behalf:</p>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>Payment processors for secure transaction handling</li>
            <li>Cloud storage providers for secure data storage</li>
            <li>Communication service providers for SMS and email delivery</li>
            <li>Analytics providers to help us understand service usage</li>
          </ul>

          <h4 className="font-semibold text-gray-800 mb-3">Healthcare Partners</h4>
          <p className="mb-4">With your consent, we may share your health information with:</p>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>Your referring physician or healthcare provider</li>
            <li>Specialists for consultation purposes</li>
            <li>Insurance companies for claim processing</li>
          </ul>

          <h4 className="font-semibold text-gray-800 mb-3">Legal Requirements</h4>
          <p className="mb-4">We may disclose your information if required to do so by law or in response to:</p>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>Court orders or legal processes</li>
            <li>Government or regulatory requests</li>
            <li>To protect our legal rights or defend against claims</li>
          </ul>

          <h4 className="font-semibold text-gray-800 mb-3">Business Transfers</h4>
          <p>In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction. We will notify you of any such change.</p>

          <h4 className="font-semibold text-gray-800 mb-3 mt-4">Third-Party SDKs and Services</h4>
          <p className="mb-4">Our mobile application integrates the following third-party services that may collect data:</p>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li><strong>Payment Gateways:</strong> Process payments securely (collects payment card details)</li>
            <li><strong>Push Notification Services:</strong> Deliver notifications (collects device tokens)</li>
            <li><strong>Analytics Services:</strong> App usage analytics (collects anonymized usage data)</li>
            <li><strong>Crash Reporting:</strong> Identify and fix app issues (collects crash logs and device info)</li>
            <li><strong>Cloud Storage:</strong> Secure file storage for reports (collects uploaded files)</li>
          </ul>
          <p className="text-sm text-gray-600 mb-4">
            These third-party services operate under their own privacy policies. We encourage you to review them.
          </p>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
            <p className="text-green-800 font-medium">
              <strong>We Do NOT Sell Your Personal Information.</strong> We do not sell, rent, or trade your personal information to third parties for their marketing purposes.
            </p>
          </div>
        </>
      )
    },
    {
      id: 'data-security',
      title: 'Data Security',
      icon: Lock,
      content: (
        <>
          <p className="mb-4">We implement appropriate technical and organizational security measures to protect your personal information, including:</p>
          
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li><strong>Encryption:</strong> All data transmitted between your device and our servers is encrypted using TLS/SSL technology</li>
            <li><strong>Secure Storage:</strong> Personal and health data is stored in encrypted databases with restricted access</li>
            <li><strong>Access Controls:</strong> Only authorized personnel with a legitimate need can access your information</li>
            <li><strong>Regular Audits:</strong> We conduct regular security assessments and vulnerability testing</li>
            <li><strong>Employee Training:</strong> Our staff receives regular training on data protection and privacy practices</li>
            <li><strong>Incident Response:</strong> We have procedures in place to detect, respond to, and recover from security incidents</li>
          </ul>

          <p className="mb-4">
            While we strive to protect your personal information, no method of transmission over the Internet or electronic storage is 100% secure. We cannot guarantee absolute security but are committed to maintaining industry-standard protections.
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-amber-800">
              <strong>Your Responsibility:</strong> Please keep your account credentials confidential and notify us immediately if you suspect any unauthorized access to your account.
            </p>
          </div>
        </>
      )
    },
    {
      id: 'data-retention',
      title: 'Data Retention',
      icon: FileText,
      content: (
        <>
          <p className="mb-4">We retain your personal information for as long as necessary to:</p>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>Provide you with our services</li>
            <li>Comply with legal obligations (medical records are retained as per applicable healthcare regulations)</li>
            <li>Resolve disputes and enforce our agreements</li>
            <li>Maintain business records for audit purposes</li>
          </ul>

          <h4 className="font-semibold text-gray-800 mb-3">Retention Periods</h4>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li><strong>Medical Records:</strong> Retained for a minimum of 10 years as per applicable medical record retention requirements</li>
            <li><strong>Account Information:</strong> Retained while your account is active and for 5 years after account closure</li>
            <li><strong>Transaction Records:</strong> Retained for 7 years for financial compliance</li>
            <li><strong>Communication Records:</strong> Retained for 3 years or as required by law</li>
          </ul>

          <p>When information is no longer needed, we securely delete or anonymize it in accordance with our data retention policies.</p>
        </>
      )
    },
    {
      id: 'your-rights',
      title: 'Your Privacy Rights',
      icon: UserCheck,
      content: (
        <>
          <p className="mb-4">You have the following rights regarding your personal information:</p>

          <h4 className="font-semibold text-gray-800 mb-3">Right to Access</h4>
          <p className="mb-4">You can request a copy of the personal information we hold about you.</p>

          <h4 className="font-semibold text-gray-800 mb-3">Right to Correction</h4>
          <p className="mb-4">You can request that we correct any inaccurate or incomplete information about you.</p>

          <h4 className="font-semibold text-gray-800 mb-3">Right to Deletion</h4>
          <p className="mb-4">You can request that we delete your personal information, subject to certain legal exceptions (e.g., medical record retention requirements).</p>

          <h4 className="font-semibold text-gray-800 mb-3">Account Deletion Request</h4>
          <p className="mb-4">
            To request deletion of your account and associated data from our mobile application:
          </p>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>Email us at <a href="mailto:info@nandadiagnostics.com" className="text-[#007897] underline">info@nandadiagnostics.com</a> with subject line &quot;Account Deletion Request&quot;</li>
            <li>Call our customer support at 011-47500500</li>
            <li>Visit any Nanda Diagnostics center with valid ID proof</li>
          </ul>
          <p className="mb-4">
            Upon receiving your deletion request, we will:
          </p>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>Verify your identity to prevent unauthorized deletion</li>
            <li>Delete your account and personal data within 30 days</li>
            <li>Send confirmation once deletion is complete</li>
            <li>Retain only data required by law (medical records as per regulatory requirements)</li>
          </ul>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
            <p className="text-amber-800">
              <strong>Note:</strong> Medical records may be retained for the legally mandated period (minimum 10 years) even after account deletion, as required by applicable healthcare regulations.
            </p>
          </div>

          <h4 className="font-semibold text-gray-800 mb-3">Right to Data Portability</h4>
          <p className="mb-4">You can request your data in a commonly used, machine-readable format.</p>

          <h4 className="font-semibold text-gray-800 mb-3">Right to Withdraw Consent</h4>
          <p className="mb-4">Where we rely on your consent to process your information, you can withdraw that consent at any time.</p>

          <h4 className="font-semibold text-gray-800 mb-3">Right to Opt-Out</h4>
          <p className="mb-4">You can opt out of receiving promotional communications by clicking the "unsubscribe" link in our emails or contacting us directly.</p>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-blue-800">
              <strong>To Exercise Your Rights:</strong> Contact us at <a href="mailto:info@nandadiagnostics.com" className="underline">info@nandadiagnostics.com</a> or call us at <a href="tel:011-47500500" className="underline">011-47500500</a>. We will respond to your request within 30 days.
            </p>
          </div>
        </>
      )
    },
    {
      id: 'consent-legal-basis',
      title: 'Consent and Legal Basis',
      icon: UserCheck,
      content: (
        <>
          <p className="mb-4">
            We process your personal and health data based on the following legal grounds:
          </p>

          <h4 className="font-semibold text-gray-800 mb-3">Explicit Consent</h4>
          <p className="mb-4">
            For health data and sensitive personal information, we obtain your explicit, informed consent before collection. This consent is:
          </p>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li><strong>Free:</strong> Given voluntarily without coercion</li>
            <li><strong>Specific:</strong> For clearly defined purposes</li>
            <li><strong>Informed:</strong> After providing clear information about data use</li>
            <li><strong>Unambiguous:</strong> Through clear affirmative action</li>
          </ul>

          <h4 className="font-semibold text-gray-800 mb-3">Purpose-Specific Consent</h4>
          <p className="mb-4">We obtain separate consent for different purposes:</p>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li><strong>Diagnostic Services:</strong> To perform tests and generate reports</li>
            <li><strong>Healthcare Provider Sharing:</strong> To share results with your referring doctor</li>
            <li><strong>Marketing Communications:</strong> To send promotional offers (optional)</li>
            <li><strong>Research/Analytics:</strong> To use anonymized data for service improvement (optional)</li>
          </ul>

          <h4 className="font-semibold text-gray-800 mb-3">Withdrawal of Consent</h4>
          <p className="mb-4">
            You may withdraw your consent at any time by contacting us. Withdrawal will not affect the lawfulness of processing done before withdrawal. Note that withdrawing consent for essential services may limit your ability to use our Services.
          </p>

          <h4 className="font-semibold text-gray-800 mb-3">Processing Without Consent (Exceptions)</h4>
          <p className="mb-4">In limited circumstances, we may process data without explicit consent:</p>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li><strong>Medical Emergency:</strong> When there is a threat to life or health</li>
            <li><strong>Legal Obligation:</strong> When required by law or court order</li>
            <li><strong>Public Health:</strong> During health emergencies or disease outbreaks</li>
            <li><strong>Vital Interests:</strong> To protect your vital interests when you cannot give consent</li>
          </ul>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-green-800 text-sm">
              <strong>Your Consent Matters:</strong> You have the right to know exactly how your data is used. We will always be transparent about our data practices and respect your choices.
            </p>
          </div>
        </>
      )
    },
    {
      id: 'grievance-redressal',
      title: 'Grievance Redressal',
      icon: AlertCircle,
      content: (
        <>
          <p className="mb-4">
            If you have any concerns, complaints, or grievances regarding the processing of your personal data, you can reach out to us through the following channels:
          </p>

          <h4 className="font-semibold text-gray-800 mb-3">Grievance Officer</h4>
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <p className="font-medium text-gray-800">Data Protection / Grievance Officer</p>
            <p className="text-gray-600 text-sm mt-2">
              <strong>Email:</strong> <a href="mailto:grievance@nandadiagnostics.com" className="text-[#007897] underline">grievance@nandadiagnostics.com</a><br />
              <strong>Phone:</strong> 011-47500500<br />
              <strong>Address:</strong> {companyInfo.address}
            </p>
          </div>

          <h4 className="font-semibold text-gray-800 mb-3">Grievance Resolution Process</h4>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>Submit your grievance via email, phone, or in writing</li>
            <li>You will receive an acknowledgment within 48 hours</li>
            <li>We will investigate and respond within 30 days</li>
            <li>If unsatisfied, you may escalate to the Data Protection Board of India</li>
          </ul>

          <h4 className="font-semibold text-gray-800 mb-3">External Remedies</h4>
          <p className="mb-4">
            If you are not satisfied with our response, you have the right to:
          </p>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>Lodge a complaint with the Data Protection Board of India</li>
            <li>Seek remedy through consumer forums</li>
            <li>Approach appropriate courts for legal relief</li>
          </ul>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-amber-800 text-sm">
              <strong>Response Timeline:</strong> We are committed to resolving all grievances within 30 days of receipt. Complex matters requiring additional investigation may take longer, and we will keep you informed of progress.
            </p>
          </div>
        </>
      )
    },
    {
      id: 'data-breach',
      title: 'Data Breach Notification',
      icon: AlertCircle,
      content: (
        <>
          <p className="mb-4">
            In the event of a personal data breach that may harm your interests, we are committed to:
          </p>

          <h4 className="font-semibold text-gray-800 mb-3">Notification to Authorities</h4>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>Notify the Data Protection Board of India within 72 hours of becoming aware of the breach</li>
            <li>Report to CERT-In within 6 hours as required for cyber security incidents</li>
            <li>Cooperate with regulatory authorities in their investigation</li>
          </ul>

          <h4 className="font-semibold text-gray-800 mb-3">Notification to You</h4>
          <p className="mb-4">If a breach is likely to result in high risk to your rights, we will:</p>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>Notify you without undue delay</li>
            <li>Describe the nature of the breach</li>
            <li>Provide contact details of our Data Protection Officer</li>
            <li>Describe likely consequences of the breach</li>
            <li>Describe measures taken or proposed to address the breach</li>
          </ul>

          <h4 className="font-semibold text-gray-800 mb-3">Our Security Measures</h4>
          <p className="mb-4">
            We maintain comprehensive security measures to prevent breaches, including encryption, access controls, regular audits, and employee training. However, no system is completely secure, and we are prepared to respond swiftly to any incident.
          </p>
        </>
      )
    },
    {
      id: 'cookies',
      title: 'Cookies and Tracking Technologies',
      icon: Eye,
      content: (
        <>
          <p className="mb-4">We use cookies and similar tracking technologies to collect and store information about your interactions with our Services.</p>

          <h4 className="font-semibold text-gray-800 mb-3">Types of Cookies We Use</h4>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li><strong>Essential Cookies:</strong> Required for the website to function properly (e.g., authentication, security)</li>
            <li><strong>Functional Cookies:</strong> Remember your preferences and settings</li>
            <li><strong>Analytics Cookies:</strong> Help us understand how visitors interact with our website</li>
          </ul>

          <h4 className="font-semibold text-gray-800 mb-3">Managing Cookies</h4>
          <p className="mb-4">You can control cookies through your browser settings. However, disabling certain cookies may limit your ability to use some features of our Services.</p>

          <h4 className="font-semibold text-gray-800 mb-3">Do Not Track</h4>
          <p>We currently do not respond to "Do Not Track" signals. However, we do not track users across third-party websites for advertising purposes.</p>
        </>
      )
    },
    {
      id: 'children',
      title: "Children's Privacy",
      icon: AlertCircle,
      content: (
        <>
          <p className="mb-4">
            Our Services are not intended for children under 18 years of age. We do not knowingly collect personal information from children under 18 without verifiable parental consent.
          </p>
          <p className="mb-4">
            For diagnostic services provided to minors, we require consent from a parent or legal guardian. The parent or guardian is responsible for providing accurate information and has the rights described in this policy on behalf of the minor.
          </p>
          <p>
            If we learn that we have collected personal information from a child under 18 without parental consent, we will take steps to delete that information as quickly as possible. If you believe we might have information from or about a child under 18, please contact us immediately.
          </p>
        </>
      )
    },
    {
      id: 'mobile-application',
      title: 'Mobile Application',
      icon: Bell,
      content: (
        <>
          <p className="mb-4">
            This section applies to your use of the Nanda Diagnostics mobile application (the &quot;App&quot;), available for download on Google Play Store and Apple App Store.
          </p>

          <h4 className="font-semibold text-gray-800 mb-3">App Development and Ownership</h4>
          <p className="mb-4">
            The Nanda Diagnostics mobile application is developed and maintained by Nanda Diagnostics Pvt. Ltd. All intellectual property rights in the App remain with Nanda Diagnostics.
          </p>

          <h4 className="font-semibold text-gray-800 mb-3">Device Permissions</h4>
          <p className="mb-4">Our App may request access to certain features on your device:</p>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li><strong>Camera:</strong> To capture and upload prescription images, medical documents, or profile photos for account verification</li>
            <li><strong>Storage:</strong> To download and save your medical reports, test results, and invoices for offline access</li>
            <li><strong>Notifications:</strong> To send you appointment reminders, test result notifications, promotional offers, and important health alerts</li>
            <li><strong>Location (Optional):</strong> To help you find the nearest Nanda Diagnostics center and enable home sample collection services</li>
            <li><strong>Phone:</strong> To enable one-tap calling to our customer support and diagnostic centers</li>
          </ul>

          <h4 className="font-semibold text-gray-800 mb-3">App Features and Data Collection</h4>
          <p className="mb-4">The App provides the following features that may collect and process your data:</p>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li><strong>Report Downloads:</strong> Access, view, and download your diagnostic test reports securely</li>
            <li><strong>Appointment Booking:</strong> Schedule tests, health packages, and home sample collection</li>
            <li><strong>Health Records:</strong> Store and manage your complete health history and previous test reports</li>
            <li><strong>Push Notifications:</strong> Receive real-time updates about your test status, appointments, and health tips</li>
            <li><strong>Payment Processing:</strong> Make secure payments for tests and packages through integrated payment gateways</li>
          </ul>

          <h4 className="font-semibold text-gray-800 mb-3">App Analytics</h4>
          <p className="mb-4">
            We may collect anonymous usage data to improve App performance, including crash reports, feature usage statistics, and session duration. This data does not personally identify you and is used solely for enhancing your experience.
          </p>

          <h4 className="font-semibold text-gray-800 mb-3">Third-Party Services in App</h4>
          <p>
            The App may integrate with third-party services for payment processing, analytics, and notifications. These services are governed by their own privacy policies, and we encourage you to review them.
          </p>
        </>
      )
    },
    {
      id: 'international',
      title: 'International Data Transfers',
      icon: Database,
      content: (
        <>
          <p className="mb-4">
            Your information may be transferred to and processed in countries other than your country of residence. These countries may have data protection laws that are different from the laws of your country.
          </p>
          <p className="mb-4">
            We take appropriate safeguards to ensure that your personal information remains protected in accordance with this Privacy Policy, including:
          </p>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>Using standard contractual clauses approved by regulatory authorities</li>
            <li>Ensuring third-party recipients maintain adequate security measures</li>
            <li>Complying with applicable data transfer regulations</li>
          </ul>
          <p>
            By using our Services, you consent to the transfer of your information to facilities located outside your country of residence.
          </p>
        </>
      )
    },
    {
      id: 'updates',
      title: 'Changes to This Privacy Policy',
      icon: Bell,
      content: (
        <>
          <p className="mb-4">
            We may update this Privacy Policy from time to time. We will notify you of any material changes by:
          </p>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>Posting the new Privacy Policy on this page</li>
            <li>Updating the "Last Updated" date at the top of this policy</li>
            <li>Sending you an email notification (for registered users)</li>
            <li>Displaying a prominent notice on our website or application</li>
          </ul>
          <p>
            We encourage you to review this Privacy Policy periodically to stay informed about how we are protecting your information. Your continued use of our Services after any changes constitutes your acceptance of the updated Privacy Policy.
          </p>
        </>
      )
    },
    {
      id: 'contact',
      title: 'Contact Us',
      icon: Mail,
      content: (
        <>
          <p className="mb-4">
            If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
          </p>
          
          <div className="bg-gray-50 rounded-lg p-6 space-y-4">
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-[#007897] mt-1 flex-shrink-0" />
              <div>
                <p className="font-semibold text-gray-800">Nanda Diagnostics</p>
                <p className="text-gray-600">{companyInfo.address}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-[#007897] flex-shrink-0" />
              <div>
                <p className="font-semibold text-gray-800">Email</p>
                <a href={`mailto:${companyInfo.email}`} className="text-[#007897] hover:underline">
                  {companyInfo.email}
                </a>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Phone className="w-5 h-5 text-[#007897] flex-shrink-0" />
              <div>
                <p className="font-semibold text-gray-800">Phone</p>
                <a href={`tel:${companyInfo.phones[0]}`} className="text-[#007897] hover:underline">
                  {companyInfo.phones[0]}
                </a>
              </div>
            </div>
          </div>

          <p className="mt-4 text-gray-600">
            For privacy-related inquiries, please include "Privacy Request" in the subject line of your email. We will respond to your inquiry within 30 days.
          </p>
        </>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-[#007897] to-[#005a73]">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center text-white">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Shield className="w-8 h-8" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Privacy Policy</h1>
            <p className="text-lg text-white/90 max-w-2xl mx-auto mb-6">
              Your privacy is important to us. This policy explains how we collect, use, and protect your personal information.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-sm text-white/80">
              <span>Last Updated: {lastUpdated}</span>
              <span className="hidden sm:inline">|</span>
              <span>Effective Date: {effectiveDate}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Table of Contents */}
      <section className="py-8 bg-white border-b">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Quick Navigation</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {sections.map((section) => (
              <a
                key={section.id}
                href={`#${section.id}`}
                className="text-sm text-[#007897] hover:text-[#005a73] hover:underline"
              >
                {section.title}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Content Sections */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4">
          <div className="space-y-12">
            {sections.map((section) => {
              const IconComponent = section.icon;
              return (
                <div
                  key={section.id}
                  id={section.id}
                  className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 scroll-mt-24"
                >
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-12 h-12 bg-[#007897]/10 rounded-xl flex items-center justify-center">
                      <IconComponent className="w-6 h-6 text-[#007897]" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">{section.title}</h2>
                  </div>
                  <div className="text-gray-600 leading-relaxed">
                    {section.content}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Final Note */}
          <div className="mt-12 bg-gradient-to-r from-[#007897] to-[#005a73] rounded-2xl p-8 text-white text-center">
            <h3 className="text-2xl font-bold mb-4">Your Trust Matters to Us</h3>
            <p className="text-white/90 mb-6 max-w-2xl mx-auto">
              We are committed to protecting your privacy and ensuring the security of your personal and health information. If you have any questions or concerns, please don&apos;t hesitate to reach out.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 bg-white text-[#007897] px-6 py-3 rounded-xl font-semibold hover:bg-gray-100 transition-colors"
              >
                <Phone className="w-5 h-5" />
                Contact Us
              </Link>
              <a
                href={`mailto:${companyInfo.email}?subject=Privacy%20Inquiry`}
                className="inline-flex items-center justify-center gap-2 bg-white/20 text-white px-6 py-3 rounded-xl font-semibold hover:bg-white/30 transition-colors"
              >
                <Mail className="w-5 h-5" />
                Email Us
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;
